/**
 * Segmented — segmented control / tab switch (design-revamp / handoff-2026-06).
 *
 * Production translation of the prototype `Segmented`
 * (`docs/GardEn/handoff-2026-06/garden/ui.jsx`). A sliding "thumb" sits over a
 * soft-primary track and animates under the active segment.
 *
 * Motion strategy: CSS-only — the thumb is an absolutely-positioned element
 * whose `left`/`width` are computed from the active index (exactly the
 * prototype's approach) and transitioned via `--g-ease`. This needs zero
 * runtime animation dependency and is automatically stilled under
 * `prefers-reduced-motion` (a media query below removes the transition);
 * packages/ui can't import the app's motion hook, so the media query is the
 * portable equivalent.
 *
 * `options` accept either `{ value, label }` objects or bare strings (the
 * string is used as both value and label), matching the prototype's
 * `o.value ?? o` / `o.label ?? o` coalescing.
 */
import { useMemo, type ReactElement } from "react";

export interface SegmentedOption {
  value: string;
  label: string;
}

export interface SegmentedProps {
  /** Options — `{ value, label }[]` or `string[]`. */
  options: ReadonlyArray<SegmentedOption | string>;
  /** Currently-selected value. */
  value: string;
  /** Selection-change handler. */
  onChange: (value: string) => void;
  /** Stretch to the full width of the container. */
  full?: boolean;
}

interface NormalizedOption {
  value: string;
  label: string;
}

function normalize(
  options: ReadonlyArray<SegmentedOption | string>,
): NormalizedOption[] {
  return options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label },
  );
}

/**
 * Renders a segmented control. `data-testid="g-segmented"` on the track; each
 * segment is `g-segmented-option` with `data-value` + `data-active`. The
 * thumb (`g-segmented-thumb`) is `aria-hidden` decoration.
 */
export function Segmented({
  options,
  value,
  onChange,
  full = false,
}: SegmentedProps): ReactElement {
  const items = useMemo(() => normalize(options), [options]);
  const count = items.length;
  const activeIndex = Math.max(
    0,
    items.findIndex((o) => o.value === value),
  );

  // Thumb geometry mirrors the prototype: a 3px inset track, so the thumb is
  // `100/n%` wide minus 6px, offset by `(idx/n)*100% + 3px`.
  const thumbStyle = {
    left: `calc(${(activeIndex / count) * 100}% + 3px)`,
    width: `calc(${100 / count}% - 6px)`,
  } as const;

  return (
    <div
      data-testid="g-segmented"
      role="radiogroup"
      className={[
        "relative grid rounded-full bg-[var(--g-primary-soft)] p-[3px]",
        full ? "w-full" : "inline-grid",
      ].join(" ")}
      style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}
    >
      <span
        data-testid="g-segmented-thumb"
        aria-hidden="true"
        className="absolute bottom-[3px] top-[3px] rounded-full bg-[var(--g-surface)] shadow-[var(--g-shadow-sm)] transition-[left] duration-150 ease-[var(--g-ease)] motion-reduce:transition-none"
        style={thumbStyle}
      />
      {items.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid="g-segmented-option"
            data-value={o.value}
            data-active={active ? "true" : "false"}
            onClick={() => onChange(o.value)}
            className={[
              "relative z-[1] h-9 border-none bg-transparent text-[13px] font-semibold transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--g-primary-soft)]",
              active ? "text-[var(--g-primary-ink)]" : "text-[var(--g-ink-2)]",
            ].join(" ")}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
